import React from "react";
import IActionOptions from "../../../model/interface/widget/option/IActionOptions";
import IconBuilder from "../../../utils/IconBuilder";
import {Spin, Tooltip} from "antd";
import Utils from "../../../utils";
import IAction from "../../../model/interface/dataStorage/IAction";
import WorkflowActionButton from "./WorkflowActionButton";
import IRestResource from "../../../model/interface/api/IRestResource";
import Action from "./Action";
import {ACTION_REJECT_REASON, IActionResult} from "../../../model/service/dataStorage/ActionsService";
import RoutesRouter from "../router/RoutesRouter";
import Button from "../../shared/button/Button";
import {RouteComponentProps, withRouter} from "react-router-dom";
import Modal from "../../shared/modal/Modal";
import LocaleText from "../settings/dictionary/LocaleText";
import {IAppState} from "../../../redux/store";
import selectors from "../../../redux/selectors";
import {connect} from "react-redux";

export type ActionButtonExecuteType = (customPayload?: any, customAction?: IAction, customResources?: IRestResource[]) => Promise<void>

interface IProps extends RouteComponentProps {
    onFinish?: (result?: IActionResult) => Promise<void>,
    disabled?: boolean
    action: IAction
    options: IActionOptions
    payload?: any
    onClick?: (execute: (customPayload?: any, customAction?: IAction, customResources?: IRestResource[]) => Promise<void>) => Promise<void>
    resources?: IRestResource[],
    className?: string
    wrapperClassName?: string,
    triggerClickRender?: boolean
    customRender?: (onClick: () => Promise<void>) => JSX.Element | string | undefined
    getText: (code: string, fallBack?: string) => string
}

interface IState {
    loading: boolean
    result?: IActionResult
}

class ActionButton extends React.Component<IProps, IState> {

    constructor(props: Readonly<IProps> | IProps) {
        super(props);
        this.state = {
            loading: false
        }
    }

    componentDidMount() {
        const {triggerClickRender} = this.props
        if (triggerClickRender) {
            this.onClick().then()
        }
    }

    render() {
        const {action, wrapperClassName, customRender} = this.props
        const {result} = this.state
        const modal = action.modal && result && result.redirect && (
            <Modal onCancel={() => this.hideModal()} visible={true} width={'75vw'} title={<LocaleText code={action.label}/>}
                   fullScreenOption={true} okButtonProps={{hidden: true}} cancelText={<LocaleText code={'general.close'}/>}>
                <RoutesRouter result={result} path={result.redirect} onActionFinish={this.onModalActionFinish} isModal={true}/>
            </Modal>
        )
        return customRender ? <>{customRender(this.onClick)}{modal}</> : <div className={"noPrint " + (wrapperClassName || '')}>
            {this.getButtonByActionType(action)}
            {modal}
        </div>
    }

    onModalActionFinish = (result?: IActionResult) => {
        if (this.props.onFinish) {
            this.props.onFinish(result).then()
            this.setState({result: undefined})
            return
        }
        return Promise.resolve()
    }

    hideModal = () => {
        this.setState({result: undefined})
        if (this.props.onFinish) {
            this.props.onFinish().then()
        }
    }

    getButtonByActionType(action: IAction) {
        switch (action.type) {
            case('workflow_state'):
                return this.renderWorkflowStateChange();
            default:
                return this.renderDefault()
        }
    }

    runAction = (customPayload?: any, customAction?: IAction, customResources?: IRestResource[]): Promise<void> => {
        const {onFinish, action, payload, history, resources} = this.props
        this.setState({loading: true})
        return Action.doAction(
            customAction || action,
            customResources || resources || null,
            history,
            {...payload, ...customPayload}
        ).then(result => {
            Action.setLastActionFormData(result.action, {...payload, ...customPayload})
            this.setState({result, loading: false})
            let promise = Promise.resolve()
            if (!action.modal) {
                if (result.redirect) {
                    promise.then(() => history.push(result.redirect!, {result}))
                }
                if (onFinish) {
                    return promise.then(() => {
                        return (!result.redirect || result.redirect === history.location.pathname) ? onFinish(result) : Promise.reject()
                    }).catch(() => Promise.reject())
                }
            }
            return promise
        }).catch((reason: string) => {
            if ([ACTION_REJECT_REASON.CANCELLED, ACTION_REJECT_REASON.TERMINATED].includes(reason)) {
                this.setState({loading: false})
                return Promise.reject()
            } else {
                return Promise.reject(reason)
            }
        })

    }

    renderWorkflowStateChange() {
        const {resources} = this.props
        return resources && resources.length > 0 && <WorkflowActionButton
            {...this.props}
            onExecute={(payload, action, resources) => this.onExecute(payload, action, resources) as Promise<void>}
        />
    }

    onExecute(payload: any, action: IAction, resources?: IRestResource[]) {
        if (this.props.onClick) {
            return this.props.onClick(() => this.runAction(payload, action, resources))
        } else {
            return this.runAction(payload, action, resources)
        }
    }

    onClick = () => {
        if (this.props.onClick) {
            return this.props.onClick(this.runAction)
        }
        return this.runAction()
    }

    renderDefault() {
        const {options, disabled, action, className, getText} = this.props
        const {loading} = this.state
        const icon = options.icon ? IconBuilder(options.icon) : undefined
        let button = (
            <Button
                className={(className || '')}
                disabled={disabled}
                loading={loading}
                type={options.type}
                danger={Utils.toBoolean(options.danger)}
                onClick={() => this.onClick()}
                style={{background: options.color, color: options.textColor}}
                icon={icon}
                size={options.size || "small"}
            >
                {typeof options.label === 'string' ? getText(options.label || '') : options.label}
            </Button>
        )
        if (options.link) {
            button = (
                <Spin spinning={loading} wrapperClassName={'d-inline-block'} size={"small"}>
                    <div className={'d-inline-block text-link ' + (className || '')} onClick={() => this.onClick()}>
                        {typeof options.label === 'string' ? getText(options.label || '') : options.label}
                    </div>
                </Spin>
            )
        }
        if (options.tooltip) {
            return (
                <Tooltip key={action.uuid!} title={getText(options.tooltip || '')}>
                    {button}
                </Tooltip>
            )
        }
        return button
    }
}

const mapStateToProps = (state: IAppState) => {
    return {
        getText: (code: string, fallback?: string) => selectors.dictionary.getMessage(state, code, fallback)

    }
}

export default connect(mapStateToProps)(withRouter(ActionButton))